import { store } from "../store";
import {
  logout,
  PermissionMap,
  tokenRefreshed,
  User,
} from "../store/slices/authSlice";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryParamValue = string | number | boolean | null | undefined;

export interface ApiRequestResponse<T = any> {
  data: T;
  message: string;
  status: number;
}

interface RefreshResponse {
  token: string;
  user?: User;
  permissions?: PermissionMap;
}

/**
 * Standard API error returned by apiRequest.
 */
export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data: any = null) {
    super(message);

    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let refreshPromise: Promise<string | null> | null = null;

/**
 * Legacy compatibility helper.
 *
 * Existing components may still call setAuthToken().
 * apiRequest itself always reads the latest token from localStorage.
 */
export const setAuthToken = (token: string | null): void => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

/**
 * Adds query parameters to an endpoint.
 */
const buildUrl = (
  endpoint: string,
  queryParams?: Record<string, QueryParamValue>,
): string => {
  if (!queryParams) {
    return endpoint;
  }

  const params = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      params.append(key, String(value));
    }
  });

  const query = params.toString();

  if (!query) {
    return endpoint;
  }

  return `${endpoint}${endpoint.includes("?") ? "&" : "?"}${query}`;
};

/**
 * Safely parses an API response.
 */
const parseResponse = async (response: Response): Promise<any> => {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

/**
 * Returns the actual response payload.
 *
 * Supports both:
 * { data: ... }
 *
 * and:
 * ...
 */
const getResponseData = <T = any>(json: any): T => {
  if (
    json &&
    typeof json === "object" &&
    Object.prototype.hasOwnProperty.call(json, "data")
  ) {
    return json.data as T;
  }

  return json as T;
};

/**
 * Checks whether the absolute 24-hour session has expired.
 */
const isSessionExpired = (): boolean => {
  const storedExpiry = localStorage.getItem("sessionExpiresAt");

  if (!storedExpiry) {
    return false;
  }

  const expiresAt = Number(storedExpiry);

  if (!Number.isFinite(expiresAt)) {
    return true;
  }

  return Date.now() >= expiresAt;
};

/**
 * Clears local authentication and redirects to login.
 */
const clearSession = (): void => {
  store.dispatch(logout());

  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

/**
 * Determines whether a failed request should attempt token refresh.
 */
const shouldAttemptRefresh = (endpoint: string): boolean => {
  return !(
    endpoint.includes("/api/auth/login") ||
    endpoint.includes("/api/auth/refresh") ||
    endpoint.includes("/api/auth/logout")
  );
};

/**
 * Uses the HttpOnly refresh-token cookie to obtain
 * a new short-lived access token.
 *
 * Multiple simultaneous 401 responses share the
 * same refresh request.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  if (isSessionExpired()) {
    return null;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const json = await parseResponse(response);

      if (!response.ok) {
        return null;
      }

      const data = getResponseData<RefreshResponse>(json);

      if (!data?.token) {
        return null;
      }

      /**
       * Replace only the access token.
       * sessionExpiresAt is intentionally
       * not changed here.
       */
      store.dispatch(
        tokenRefreshed({
          token: data.token,
          user: data.user,
          permissions: data.permissions,
        }),
      );

      return data.token;
    } catch (error) {
      console.error("Unable to refresh access token:", error);

      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Executes an HTTP request using the supplied access token.
 */
const executeRequest = async (
  url: string,
  method: HttpMethod,
  body: unknown,
  token: string | null,
): Promise<Response> => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method,
    headers,
    credentials: "include",
  };

  if (body !== undefined && method !== "GET") {
    options.body = JSON.stringify(body);
  }

  return fetch(url, options);
};

/**
 * Generic fetch wrapper for all API calls.
 *
 * Existing calls can continue using:
 *
 * apiRequest(...)
 *
 * New code can optionally use:
 *
 * apiRequest<MyResponse>(...)
 */
export async function apiRequest<T = any>(
  endpoint: string,
  method: HttpMethod = "GET",
  body?: unknown,
  queryParams?: Record<string, QueryParamValue>,
): Promise<ApiRequestResponse<T>> {
  const url = buildUrl(endpoint, queryParams);

  /**
   * Do not allow an already expired
   * absolute session to continue.
   */
  if (isSessionExpired() && shouldAttemptRefresh(endpoint)) {
    clearSession();

    throw new ApiError(401, "Session expired");
  }

  let token = localStorage.getItem("token");

  let response = await executeRequest(url, method, body, token);

  /**
   * Access token expired.
   *
   * Attempt one refresh before
   * ending the user's session.
   */
  if (response.status === 401 && shouldAttemptRefresh(endpoint)) {
    const refreshedToken = await refreshAccessToken();

    if (!refreshedToken) {
      clearSession();

      throw new ApiError(401, "Session expired");
    }

    token = refreshedToken;

    /**
     * Retry the original request exactly once
     * using the refreshed access token.
     */
    response = await executeRequest(url, method, body, token);
  }

  const json = await parseResponse(response);

  const message =
    typeof json?.message === "string"
      ? json.message
      : response.ok
        ? "Success"
        : "Something went wrong";

  /**
   * Authentication still failed after refresh.
   */
  if (response.status === 401) {
    clearSession();

    throw new ApiError(401, message || "Session expired", json?.data ?? null);
  }

  if (!response.ok) {
    throw new ApiError(response.status, message, json?.data ?? null);
  }

  return {
    data: getResponseData<T>(json),
    message,
    status: response.status,
  };
}
